import React, { useEffect, useState, useRef } from "react";
import styles from "./Dashboard.module.css";
import { useSelector } from "react-redux";
import { RootState } from "../../Services/Store";
import {
  MdFactory,
  MdAssignment,
  MdInventory,
  MdAccessTime,
  MdPayment,
  MdWarningAmber,
} from "react-icons/md";
import { FiInfo } from "react-icons/fi";
import WebService from "../../Services/WebService";
import { useNavigate } from "react-router-dom";
import TaskCountBoxes from "../../TaskManagement/TaskCountBoxes";

// --- Pills ---
const PriorityPill = ({ p }: { p: string }) => {
  let color = "#f4e4a3";
  let text = "#473900";
  if (p?.toLowerCase() === "high") { color = "#ffe0e0"; text = "#e43e2d"; }
  if (p?.toLowerCase() === "medium") { color = "#ffe9d2"; text = "#a15c07"; }
  if (p?.toLowerCase() === "low") { color = "#e6f6e6"; text = "#219a1d"; }
  return <span style={{
    background: color,
    color: text,
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 12,
    padding: "3px 12px",
    marginLeft: 5,
    marginRight: 2,
  }}>{p || "—"}</span>;
};
const StatusPill = ({ s }: { s: string }) => {
  let color = "#ececec";
  let text = "#333";
  if (s?.toLowerCase().includes("progress") || s?.toLowerCase().includes("process")) { color = "#e6f0ff"; text = "#2077cf"; }
  if (s?.toLowerCase().includes("complete")) { color = "#e3fce8"; text = "#1e8542"; }
  if (s?.toLowerCase().includes("not started")) { color = "#f6f6f7"; text = "#848484"; }
  if (s?.toLowerCase().includes("pending")) { color = "#fff5e1"; text = "#d48b18"; }
  return <span style={{
    background: color,
    color: text,
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 12,
    padding: "3px 10px",
    marginLeft: 5,
    marginRight: 2,
  }}>{s || "—"}</span>;
};

// --- Dashboard Main ---
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  useSelector((state: RootState) => state.auth.access || []);
  const user = useSelector((state: RootState) => state.auth.user);
  const userId = user?.user_id;
  const userType = user?.user_type?.toUpperCase?.() || ""; 



  // Initialize dates to last one year
  const getLastYearDates = () => {
    const today = new Date();
    const to = today.toISOString().slice(0, 10);
    const lastYear = new Date(today);
    lastYear.setFullYear(today.getFullYear() - 1);
    const from = lastYear.toISOString().slice(0, 10);
    return { from, to };
  };

  const initialDates = getLastYearDates();

  // Date Filters
  const [fromDate, setFromDate] = useState<string>(initialDates.from);
  const [toDate, setToDate] = useState<string>(initialDates.to);
  const [dateDropdownValue, setDateDropdownValue] = useState<string>("year");
  const [isInitialized, setIsInitialized] = useState(false);

  // Data States
  const [employeeTasks, setEmployeeTasks] = useState<any[]>([]);
  const [myTasks, setMyTasks] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [taskError, setTaskError] = useState<string | null>(null);

  // Orders Quick Stats
  const [orderStats, setOrderStats] = useState<any>({});
  const [newOrders, setNewOrders] = useState<any[]>([]);
  const [pendingOrders, setPendingOrders] = useState<any[]>([]);

  // Payments Data
  const [paymentStats, setPaymentStats] = useState<any>({});
  const [overdueOrders, setOverdueOrders] = useState<any[]>([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
 // add new state
const [workStats, setWorkStats] = useState<any>({});

  // Tooltip for Description
  const [descOpenIdx, setDescOpenIdx] = useState<number | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // --- Date Filter ---
useEffect(() => {
  if (!isInitialized) {
    setIsInitialized(true);
    fetchAllData(initialDates.from, initialDates.to);
  }
  // eslint-disable-next-line
}, []);


  // Hide tooltip when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setDescOpenIdx(null);
      }
    }
    if (descOpenIdx !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [descOpenIdx]);

  // Build API URL with query params
  function buildApiUrl(base: string, params: Record<string, string | number>): string {
    const entries = Object.entries(params).filter(([, v]) => v);
    if (entries.length === 0) return base;
    const query = entries.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    return `${base}?${query}`;
  }

  // --- Fetch all dashboard data ---
  function fetchAllData(ffrom = fromDate, tto = toDate) {
    // --- Work Summary: Tasks ---
    setTasksLoading(true);
    setTaskError(null);
    WebService.getAPI({ action: buildApiUrl("/v1/door-management/dashboard/work-summary", { from_date: ffrom, to_date: tto }) })
      .then((res: any) => {
        const data = res?.data ?? {};
         setWorkStats(data);
        setEmployeeTasks(Array.isArray(res?.data?.employee_tasks) ? res.data.employee_tasks : []);
        setMyTasks(Array.isArray(res?.data?.my_tasks) ? res.data.my_tasks : []);
      })
      .catch(() => setTaskError("Failed to load tasks"))
      .finally(() => setTasksLoading(false));

    // --- Order Summary: Quick Stats & Tables ---
    setOrdersLoading(true);
    setOrderError(null);
    WebService.getAPI({ action: buildApiUrl("/v1/door-management/dashboard/order-summary", { from_date: ffrom, to_date: tto }) })
      .then((res: any) => {
        setOrderStats(res.data || {});
        
        setNewOrders(Array.isArray(res?.data?.new_sale_orders) ? res.data.new_sale_orders : []);
        setPendingOrders(Array.isArray(res?.data?.pending_sale_orders) ? res.data.pending_sale_orders : []);
      })
      .catch(() => setOrderError("Failed to load orders"))
      .finally(() => setOrdersLoading(false));

    // --- Payment Summary: Payment Table & Overdue Stats ---
    setPaymentLoading(true);
    setPaymentError(null);
    WebService.getAPI({ action: buildApiUrl("/v1/door-management/dashboard/payment-summary", { from_date: ffrom, to_date: tto }) })
      .then((res: any) => {
        setPaymentStats(res.data || {});
        setOverdueOrders(Array.isArray(res?.data?.overdue_orders) ? res.data.overdue_orders : []);
      })
      .catch(() => setPaymentError("Failed to load payments"))
      .finally(() => setPaymentLoading(false));
  }

  // Fetch on date change (only after initialization)
  useEffect(() => {
    if (isInitialized && fromDate && toDate) {
      fetchAllData(fromDate, toDate);
    }
    // eslint-disable-next-line
  }, [fromDate, toDate]);

  // --- Dropdown handler ---
  const handleDateDropdownChange = (val: string) => {
    setDateDropdownValue(val);
    let from = "";
    let to = "";
    const today = new Date();
    if (val === "today") {
      from = today.toISOString().slice(0, 10);
      to = today.toISOString().slice(0, 10);
    } else if (val === "week") {
      to = today.toISOString().slice(0, 10);
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      from = weekAgo.toISOString().slice(0, 10);
    } else if (val === "month") {
      to = today.toISOString().slice(0, 10);
      const monthAgo = new Date(today);
      monthAgo.setMonth(today.getMonth() - 1);
      from = monthAgo.toISOString().slice(0, 10);
    } else if (val === "3months") {
      to = today.toISOString().slice(0, 10);
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(today.getMonth() - 3);
      from = threeMonthsAgo.toISOString().slice(0, 10);
    } else if (val === "year") {
      to = today.toISOString().slice(0, 10);
      const yearAgo = new Date(today);
      yearAgo.setFullYear(today.getFullYear() - 1);
      from = yearAgo.toISOString().slice(0, 10);
    }
    setFromDate(from);
    setToDate(to);
  };

  // --- Navigation ---
  const goToSalesTab = (statusTab: string) => {
    localStorage.setItem("sales_active_tab", statusTab);
    navigate("/sales");
  };
  const handleViewAllEmployeeTasks = () => navigate("/task-management");
  const handleViewAllMyTasks = () => { if (userId) navigate(`/task-management/${userId}`); };
  const openOrder = (orderId?: string | number) => {
    if (orderId) navigate(`/saleorder/${orderId}`);
  };
  // TaskManagement opens the detail panel from ?task=<id>
  const openTask = (task: any, base = "/task-management") => {
    if (task?.id) navigate(`${base}?task=${task.id}`);
  };

  // --- Quick Stats Data ---
  const quickStats = [
    {
      icon: <MdInventory style={{ color: "#44c7f3", fontSize: 25 }} />,
      label: "New Orders",
      value: String(
        orderStats?.new_sale_orders_count ??
          (Array.isArray(orderStats?.new_sale_orders) ? orderStats.new_sale_orders.length : 0)
      ).padStart(2, "0"),
      accent: "#d3f4ff",
      onClick: () => goToSalesTab("PLACED"),
    },
    {
      icon: <MdAssignment style={{ color: "#b795ff", fontSize: 24 }} />,
      label: "Total Orders",
      value: String(
        orderStats?.total_sale_orders_count ?? orderStats?.total_orders ?? 0
      ).padStart(2, "0"),
      accent: "#ede7fe",
      onClick: () => goToSalesTab("ALL"),
    },
    {
      icon: <MdFactory style={{ color: "#FFD600", fontSize: 25 }} />,
      label: "In Production",
      value: String(
        orderStats?.in_production_sale_orders_count ??
          (Array.isArray(orderStats?.pending_sale_orders) ? orderStats.pending_sale_orders.length : 0)
      ).padStart(2, "0"),
      accent: "#fffbe6",
      onClick: () => goToSalesTab("IN_PROCESSING"),
    },
    {
      icon: <MdAccessTime style={{ color: "#ffb300", fontSize: 23 }} />,
      label: "All Employee Tasks",
      value: String(
        workStats?.all_emp_pen_inpr_tasks_count ??
          ((workStats?.pending_tasks ?? 0) + (workStats?.in_progress_tasks ?? 0))
      ).padStart(2, "0"),
      accent: "#fff5e1",
      onClick: () => {
        if (userType === "EMPLOYEE" && userId) {
          navigate(`/task-management/${userId}`);
        } else {
          navigate("/task-management");
        }
      },
    },
    {
      icon: <MdWarningAmber style={{ color: "#e46a18", fontSize: 24 }} />,
      label: "Overdue Orders",
      value: String(
        paymentStats?.overdue_orders_count ?? paymentStats?.overdue_count ?? 0
      ).padStart(2, "0"),
      accent: "#fff3e6",
      onClick: () => navigate("/accounts-management"),
    },
    {
      icon: <MdPayment style={{ color: "#e43e2d", fontSize: 24 }} />,
      label: "Pending Payments",
      value: String( orderStats?.pending_payments_sale_orders_count ?? 0).padStart(2, "0"),
      accent: "#ffeaea",
      onClick: () => navigate("/accounts-management"),
    },
  ];

  // --- Hide Employee Tasks if Employee ---
  const showEmployeeTask = userType !== "EMPLOYEE";

  // --- Helper Date Formatter ---
  const formatDate = (dt?: string | null) => {
    if (dt == null) return "—";
    const raw = String(dt).trim();
    if (!raw || raw === "0001-01-01T00:00:00Z" || raw.toLowerCase() === "null") {
      return "—";
    }
    const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (ymd) {
      const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
      if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-GB");
    }
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
  };

  const taskIdOf = (task: any) =>
    task?.task_id ?? task?.id ?? "—";

  const assigneeOf = (task: any) =>
    task?.assign_to_username || task?.assign_to_name || "—";

  function formatMoney(value: unknown): string {
    if (typeof value === "number" && Number.isFinite(value)) {
      return `₹${value.toLocaleString("en-IN")}`;
    }
    if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
      return `₹${Number(value).toLocaleString("en-IN")}`;
    }
    return "₹0";
  }

  function getCustomerCell(row: any) {
    if (row.company_name && row.company_name.trim()) return row.company_name;
    if (row.username && row.username.trim()) return row.username;
    return "—";
  }

  return (
    <div className={styles.root}>
      <main className={styles.mainContent}>
        {/* ==== Quick Stats Row ==== */}
<div className={styles.quickStatsRow} style={{ gridTemplateColumns: "repeat(6, 1fr)" }}>
  {quickStats.map((stat) => (
    <div
      className={`${styles.statCard} ${stat.label === "Pending Payments" ? styles.pendingPayment : ""}`}
      key={stat.label}
      style={{ 
        background: stat.accent,
        cursor: "pointer"
      }}
      onClick={stat.onClick}
    >
      <div className={styles.statCardRow}>
        <span className={styles.statValue}>{stat.value}</span>
        <span className={styles.statIcon}>{stat.icon}</span>
      </div>
      <div className={styles.statLabel}>{stat.label}</div>
    </div>
  ))}
</div>
        <div className={styles.dateDropdownWrapper}>
          <select
            className={styles.dateDropdown}
            value={dateDropdownValue}
            onChange={e => handleDateDropdownChange(e.target.value)}
          >
            <option value="today">Today</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="3months">Last 3 Months</option>
            <option value="year">Last Year</option>
          </select>
        </div>

        {/* Main dashboard grid */}
        <div className={styles.grid}>
          {/* Left Side */}
          <div className={styles.leftCol}>
            {/* ---- Employee Task Section ---- */}
            {showEmployeeTask && (
              <section className={styles.employeeTask}>
                <div className={styles.sectionHeader} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  Employee Task
                  <span
                    style={{ color: "#186ec9", fontWeight: 500, fontSize: 13, cursor: "pointer" }}
                    onClick={handleViewAllEmployeeTasks}
                  >
                    View All &rarr;
                  </span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table className={styles.myWorkTable}>
                    <thead>
                      <tr>
                        <th>Task ID</th>
                        <th>Task Name</th>
                        <th></th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Assigned To</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasksLoading ? (
                        <tr>
                          <td colSpan={8} style={{ textAlign: "center", padding: 30, color: "#aaa" }}>Loading...</td>
                        </tr>
                      ) : taskError ? (
                        <tr>
                          <td colSpan={8} style={{ textAlign: "center", color: "#c00", padding: 20 }}>{taskError}</td>
                        </tr>
                      ) : employeeTasks.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ textAlign: "center", color: "#ccc", padding: 20 }}>No tasks found</td>
                        </tr>
                      ) : (
                        employeeTasks.slice(0, 5).map((task, i) => (
                          <tr key={task.id || task.task_id || i}>
                            <td>
                              <span className={styles.idLink} onClick={() => openTask(task)}>
                                {taskIdOf(task)}
                              </span>
                            </td>
                            <td style={{ fontWeight: 600, color: "#27324e" }}>
                              {task.subject || "—"}
                            </td>
                            <td style={{ position: "relative", width: 30 }}>
                              {!!(task.Description || task.description) && (
                                <span
                                  style={{
                                    marginLeft: 2,
                                    cursor: "pointer",
                                    display: "inline-block",
                                    verticalAlign: "middle",
                                  }}
                                  onClick={e => {
                                    e.stopPropagation();
                                    setDescOpenIdx(descOpenIdx === i ? null : i);
                                  }}
                                >
                                  <FiInfo className={styles.infoTinyIcon} />
                                </span>
                              )}
                              {descOpenIdx === i && (
                                <div
                                  ref={tooltipRef}
                                  style={{
                                    position: "absolute",
                                    left: 24,
                                    top: 18,
                                    zIndex: 20,
                                    background: "#222",
                                    color: "#fff",
                                    fontSize: 12,
                                    fontWeight: 400,
                                    padding: "8px 13px",
                                    borderRadius: 7,
                                    boxShadow: "0 3px 24px #00000014",
                                    minWidth: 180,
                                    maxWidth: 340,
                                    pointerEvents: "auto",
                                    whiteSpace: "pre-line",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {task.Description || task.description}
                                </div>
                              )}
                            </td>
                            <td>
                              <PriorityPill p={task.priority || "—"} />
                            </td>
                            <td>
                              <StatusPill s={task.status || "—"} />
                            </td>
                            <td style={{ fontSize: 13, color: "#767676" }}>
                              {assigneeOf(task)}
                            </td>
                            <td style={{ fontSize: 13, color: "#767676" }}>
                              {formatDate(task.start_date)}
                            </td>
                            <td style={{ fontSize: 13, color: "#767676" }}>
                              {formatDate(task.end_date)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
            {/* ---- My Work Section ---- */}
              <section className={styles.myWork}>
                <div
                  className={styles.sectionHeader}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    My Work
                    <span className={styles.countPill}>
                        | Pending Task: {String(workStats?.my_pending_tasks_count ?? 0).padStart(2, "0")}
                    </span>
                  </div>

                  <span
                    style={{ color: "#186ec9", fontWeight: 500, fontSize: 13, cursor: "pointer" }}
                    onClick={handleViewAllMyTasks}
                  >
                    View All &rarr;
                  </span>
                </div>
              <TaskCountBoxes counts={workStats?.my_status_counts} onSelect={handleViewAllMyTasks} />
              <div style={{ overflowX: "auto" }}>
                <table className={styles.myWorkTable}>
                  <thead>
                    <tr>
                      <th>Task Name</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Assigned To</th>
                      <th>Start date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasksLoading ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", padding: 30, color: "#aaa" }}>Loading...</td>
                      </tr>
                    ) : taskError ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", color: "#c00", padding: 20 }}>{taskError}</td>
                      </tr>
                    ) : myTasks.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", color: "#ccc", padding: 20 }}>No tasks found</td>
                      </tr>
                    ) : (
                      myTasks.slice(0, 5).map((task, i) => (
                        <tr key={task.id || task.task_id || i}>
                          <td style={{ fontWeight: 600, color: "#27324e" }}>
                            <span
                              className={styles.idLink}
                              onClick={() => openTask(task, userId ? `/task-management/${userId}` : "/task-management")}
                            >
                              {task.subject || "—"}
                            </span>
                          </td>
                          <td>
                            <PriorityPill p={task.priority || "—"} />
                          </td>
                          <td>
                            <StatusPill s={task.status || "—"} />
                          </td>
                          <td style={{ fontSize: 13, color: "#767676" }}>
                            {assigneeOf(task)}
                          </td>
                          <td style={{ fontSize: 13, color: "#767676" }}>
                            {formatDate(task.start_date)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
            {/* ---- Pending Orders ---- */}
            <section className={styles.pendingOrders}>
              <div className={styles.sectionHeader}>Pending Orders</div>
              <div style={{ overflowX: "auto" }}>
                <table className={styles.myWorkTable}>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Order By</th>
                      <th>Final Amount</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordersLoading ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", padding: 30, color: "#aaa" }}>Loading...</td>
                      </tr>
                    ) : orderError ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", color: "#c00", padding: 20 }}>{orderError}</td>
                      </tr>
                    ) : pendingOrders.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", color: "#ccc", padding: 20 }}>No pending orders</td>
                      </tr>
                    ) : (
                      pendingOrders.slice(0, 5).map((order, idx) => (
                        <tr key={order.sale_order_id || idx}>
                          <td>
                            <span className={styles.idLink} onClick={() => openOrder(order.sale_order_id)}>
                              {order.sale_order_id}
                            </span>
                          </td>
                          <td>{order.username || "—"}</td>
                          <td>₹{order.final_amount?.toLocaleString?.() ?? "—"}</td>
                          <td>{formatDate(order.created_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
            {/* Payment Section */}
            <section className={styles.payment}>
              <div className={styles.sectionHeader}>Overdue Payments</div>
              <div style={{ overflowX: "auto" }}>
                <table className={styles.myWorkTable}>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer/Company Name</th>
                      <th>Final Amount</th>
                      <th>Paid Amount</th>
                      <th>Due Amount</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentLoading ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: "center", padding: 30, color: "#aaa" }}>Loading...</td>
                      </tr>
                    ) : paymentError ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: "center", color: "#c00", padding: 20 }}>{paymentError}</td>
                      </tr>
                    ) : overdueOrders.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ textAlign: "center", color: "#ccc", padding: 20 }}>No payment history</td>
                      </tr>
                    ) : (
                      overdueOrders.slice(0, 5).map((row, idx) => (
                        <tr key={row.sale_order_id || idx}>
                          <td>
                            <span className={styles.idLink} onClick={() => openOrder(row.sale_order_id)}>
                              {row.sale_order_id}
                            </span>
                          </td>
                          <td>{getCustomerCell(row)}</td>
                          <td>{formatMoney(row.final_amount)}</td>
                          <td>{formatMoney(row.paid_amount ?? row.total_paid_amount)}</td>
                          <td>{formatMoney(row.due_amount)}</td>
                          <td>{formatDate(row.created_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
            {/* Reports */}
            {/* <section className={styles.reports}>
              <div className={styles.sectionHeader}>Reports</div>
            </section> */}
          </div>

          {/* Right Side */}
          <aside className={styles.rightCol}>
            {/* ---- New Orders ---- */}
            <section className={styles.newOrders}>
              <div className={styles.sectionHeader}>New Orders</div>
              <div style={{ overflowX: "auto" }}>
                <table className={styles.myWorkTable}>
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Order By</th>
                      <th>Final Amount</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordersLoading ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", padding: 30, color: "#aaa" }}>Loading...</td>
                      </tr>
                    ) : orderError ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", color: "#c00", padding: 20 }}>{orderError}</td>
                      </tr>
                    ) : newOrders.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", color: "#ccc", padding: 20 }}>No new orders</td>
                      </tr>
                    ) : (
                      newOrders.slice(0, 5).map((order, idx) => (
                        <tr key={order.sale_order_id || idx}>
                          <td>
                            <span className={styles.idLink} onClick={() => openOrder(order.sale_order_id)}>
                              {order.sale_order_id}
                            </span>
                          </td>
                          <td>{order.username || "—"}</td>
                          <td>₹{order.final_amount?.toLocaleString?.() ?? "—"}</td>
                          <td>{formatDate(order.created_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>
            {/* Stock Details */}
            {/* <section className={styles.stockDetails}>
              <div className={styles.sectionHeader}>Stock Details</div>
            </section> */}
          </aside>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
